import { webTierDisplayName } from "@/app/constants/newPricing2026";

export type SubscriptionAnalyticsPayload = {
  value: number;
  currency: string;
  plan: string;
  tier: string;
  interval: string;
};

// Stripe amounts are minor units (cents) and its currency is lowercase; GA4 wants a decimal major-unit
// `value` and an ISO-4217 upper-case `currency`. `plan` is the readable catalog name when the tier is a
// known 2026 web lookup_key, else the raw tier so the report still identifies the plan.
export function buildSubscriptionAnalyticsPayload(input: {
  amount: number;
  currency: string;
  tier: string;
  interval: string;
}): SubscriptionAnalyticsPayload {
  // A subscription can arrive without a lookup_key/nickname (empty tier); keep the GA4 row readable
  // rather than reporting an empty plan/tier.
  const tier = input.tier || "unknown";
  return {
    // `/100` assumes a two-decimal currency. Every 2026 web price is USD, so this holds today; a
    // zero-decimal currency (JPY/KRW) would need the currency's exponent instead.
    value: input.amount / 100,
    currency: input.currency.toUpperCase(),
    plan: webTierDisplayName(tier) ?? tier,
    tier,
    interval: input.interval,
  };
}

// A GA4 standard-ecommerce line item. begin_checkout and purchase both carry one item for the plan
// being bought — https://developers.google.com/analytics/devguides/collection/ga4/reference/events
// `item_category` holds the billing interval so GA4 ecommerce reports can split revenue by
// month vs year, the load-bearing cut for a subscription business.
export type Ga4EcommerceItem = {
  item_id: string;
  item_name: string;
  item_category: string;
  price: number;
  quantity: number;
};

export type BeginCheckoutParams = {
  currency: string;
  value: number;
  items: Ga4EcommerceItem[];
};

export type PurchaseParams = {
  transaction_id: string;
  currency: string;
  value: number;
  items: Ga4EcommerceItem[];
};

// A subscription checkout buys exactly one plan, so the ecommerce item list is always a single row:
// item_id = tier (the lookup_key), item_name = the readable plan name, item_category = the billing
// interval, price = the major-unit value.
function subscriptionItems(
  payload: SubscriptionAnalyticsPayload,
): Ga4EcommerceItem[] {
  return [
    {
      item_id: payload.tier,
      item_name: payload.plan,
      item_category: payload.interval,
      price: payload.value,
      quantity: 1,
    },
  ];
}

// begin_checkout fires before the redirect to Stripe, so its `value` is the LIST price (any coupon
// is applied later at Stripe); purchase then carries the settled value. A begin_checkout > purchase
// gap is the expected GA4 funnel shape, not a bug. Note the settled value is `session.amount_total`,
// which is tax-inclusive: if `automatic_tax` is ever enabled on checkout, purchase revenue starts
// including tax and would need a separate GA4 `tax` param to stay comparable.
export function buildBeginCheckoutParams(
  payload: SubscriptionAnalyticsPayload,
): BeginCheckoutParams {
  return {
    currency: payload.currency,
    value: payload.value,
    items: subscriptionItems(payload),
  };
}

// purchase reuses the settled subscription payload and adds `transaction_id` (the Stripe Checkout
// session id) so GA4 can attribute and reconcile the transaction. The real once-per-checkout guard is
// the localStorage dedup key in the callback, not this id.
export function buildPurchaseParams(
  payload: SubscriptionAnalyticsPayload,
  transactionId: string,
): PurchaseParams {
  return {
    transaction_id: transactionId,
    currency: payload.currency,
    value: payload.value,
    items: subscriptionItems(payload),
  };
}
